import React, { useState, useEffect, useRef } from 'react';
import { Headset, X, Send, Paperclip, Loader2, MessageCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';

const LiveChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize or fetch conversation when chat opens
  useEffect(() => {
    if (isOpen && user) {
      initializeConversation();
      // Start polling for new messages
      pollIntervalRef.current = setInterval(fetchMessages, 5000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, user]);

  // Mark messages as read when chat opens
  useEffect(() => {
    if (isOpen && conversation?.id) {
      markMessagesAsRead();
    }
  }, [isOpen, conversation?.id, messages.length]);

  const initializeConversation = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await api.post('/chat/conversations', {
        user_id: user.user_id || user.id,
        user_email: user.email,
        user_name: user.full_name || user.name || user.email?.split('@')[0] || 'User',
        subject: 'Support Request'
      });
      
      setConversation(response.data);
      setMessages(response.data.messages || []);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error initializing conversation:', error);
      toast.error('Failed to connect to support chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!conversation?.id) return;
    
    try {
      const response = await api.get(`/chat/conversations/${conversation.id}/messages`);
      const newMessages = response.data || [];
      
      // Check for new admin messages
      const adminMessages = newMessages.filter(m => m.sender_type === 'admin');
      const prevAdminMessages = messages.filter(m => m.sender_type === 'admin');
      
      if (adminMessages.length > prevAdminMessages.length && !isOpen) {
        setUnreadCount(prev => prev + (adminMessages.length - prevAdminMessages.length));
      }
      
      setMessages(newMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const markMessagesAsRead = async () => {
    if (!conversation?.id) return;
    
    try {
      await api.post(`/chat/conversations/${conversation.id}/mark-read?reader_type=user`);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation?.id || sending) return;
    
    setSending(true);
    try {
      const response = await api.post(
        `/chat/conversations/${conversation.id}/messages?sender_type=user&sender_id=${user.user_id || user.id}&sender_name=${encodeURIComponent(user.full_name || user.name || 'User')}`,
        { content: newMessage }
      );
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !conversation?.id) return;
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('chat_id', conversation.id);
      
      const uploadResponse = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Send message with attachment
      const response = await api.post(
        `/chat/conversations/${conversation.id}/messages?sender_type=user&sender_id=${user.user_id || user.id}&sender_name=${encodeURIComponent(user.full_name || user.name || 'User')}`,
        {
          content: '',
          attachment_url: uploadResponse.data.url,
          attachment_name: uploadResponse.data.filename,
          attachment_type: uploadResponse.data.content_type
        }
      );
      
      setMessages(prev => [...prev, response.data]);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderAttachment = (message) => {
    if (!message.attachment_url) return null;
    
    const isImage = message.attachment_type?.startsWith('image/');
    
    if (isImage) {
      return (
        <img 
          src={message.attachment_url} 
          alt={message.attachment_name || 'Attachment'}
          className="max-w-full rounded-lg mt-2 max-h-48 object-contain cursor-pointer"
          onClick={() => window.open(message.attachment_url, '_blank')}
        />
      );
    }
    
    return (
      <a 
        href={message.attachment_url}
        download={message.attachment_name}
        className="flex items-center gap-2 mt-2 p-2 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
      >
        <Paperclip className="h-4 w-4" />
        <span className="text-sm truncate">{message.attachment_name || 'Download file'}</span>
      </a>
    );
  };

  // Don't render if user is not logged in
  if (!user) return null;

  return (
    <>
      {/* Chat Widget Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 w-14 h-14 bg-purple-600 hover:bg-purple-700 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        aria-label="Open live chat"
      >
        {isOpen ? (
          <ChevronDown className="h-6 w-6 text-white" />
        ) : (
          <>
            <Headset className="h-6 w-6 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-36 md:bottom-24 right-4 md:right-6 z-50 w-[calc(100%-2rem)] md:w-96 h-[28rem] bg-gray-900 rounded-2xl shadow-2xl border border-gray-800 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-purple-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                <Headset className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Live Support</h3>
                <p className="text-purple-200 text-xs">We typically reply within minutes</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-950">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <MessageCircle className="h-12 w-12 text-gray-600 mb-3" />
                <p className="text-gray-400 text-sm">No messages yet</p>
                <p className="text-gray-500 text-xs mt-1">Send a message to start the conversation</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                      message.sender_type === 'user'
                        ? 'bg-purple-600 text-white rounded-br-md'
                        : 'bg-gray-800 text-gray-100 rounded-bl-md'
                    }`}
                  >
                    {message.sender_type === 'admin' && (
                      <p className="text-xs text-purple-400 mb-1 font-medium">{message.sender_name}</p>
                    )}
                    {message.content && (
                      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                    )}
                    {renderAttachment(message)}
                    <p className={`text-xs mt-1 ${
                      message.sender_type === 'user' ? 'text-purple-200' : 'text-gray-500'
                    }`}>
                      {formatTime(message.created_at)}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={sendMessage} className="p-3 bg-gray-900 border-t border-gray-800">
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.txt"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !conversation}
                className="p-2 text-gray-400 hover:text-purple-400 transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Paperclip className="h-5 w-5" />
                )}
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-800 text-white text-sm px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-500"
                disabled={!conversation}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending || !conversation}
                className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
